import {
    commentIndicatesCricketMatchResult,
    isMatchFinishedStatus,
} from "../../Trader/Cricket/decimalEventStatus";
import { isBullionPlayFullyDone } from "../../MyGames/domain";
import type { BullionLobbyFixturePhase } from "./bullionRawMatch";
import { bullionRoomResolvedFixturePhase } from "./bullionRawMatch";
import { findBullionLobbyGamesForMatchId } from "./bullionMatchLandingLobby";
import type { BullionLobbyUserActiveGame, BullionRoom } from "./types";

export type BullionFixtureSocketSignals = {
    eventStatus?: unknown;
    status?: string;
    game_phase?: string;
    matchComment?: string;
    scorecardData?: unknown;
};

export type BullionFixtureContestSignals = {
    gameStatuses?: Array<string | null | undefined>;
    plays?: Array<{
        settled?: boolean;
        settledMarkets?: number;
        totalMarkets?: number;
    } | null>;
};

function scorecardEventStatus(scorecardData: unknown): unknown {
    if (!scorecardData || typeof scorecardData !== "object") return null;
    const es = (scorecardData as Record<string, unknown>).event_status;
    return es != null && es !== "" ? es : null;
}

/** Cricket feed / scorecard / commentary signals that the fixture is over (not scheduled or in-play). */
export function bullionFixtureFinishedFromSocketSignals(
    feed: BullionFixtureSocketSignals | null | undefined
): boolean {
    if (!feed) return false;
    const { eventStatus, status, game_phase, matchComment, scorecardData } = feed;
    const scorecardEv = scorecardEventStatus(scorecardData);
    if (
        isMatchFinishedStatus(eventStatus) ||
        isMatchFinishedStatus(scorecardEv) ||
        commentIndicatesCricketMatchResult(matchComment)
    ) {
        return true;
    }
    const feedStatus = String(status ?? "").trim();
    if (feedStatus !== "" && isMatchFinishedStatus(feedStatus)) return true;
    const uLive = feedStatus.toUpperCase();
    if (
        uLive.includes("MATCH COMPLETE") ||
        uLive.includes("EVENT CLOSED") ||
        uLive.includes("MATCH ABANDONED")
    ) {
        return true;
    }
    const gp = String(game_phase ?? "").trim();
    if (/\bevent\s*closed\b/i.test(gp) || /\bmatch\s*complete\b/i.test(gp)) return true;
    return false;
}

/** Visible BULLION contests / plays on this fixture all closed out. */
export function bullionFixtureFinishedFromContestSignals(
    signals: BullionFixtureContestSignals | null | undefined
): boolean {
    if (!signals) return false;
    const statuses = (signals.gameStatuses ?? [])
        .map((s) => String(s ?? "").trim().toLowerCase())
        .filter(Boolean);
    if (statuses.length > 0 && statuses.every((s) => s === "settled" || s === "cancelled")) {
        return true;
    }
    const plays = (signals.plays ?? []).filter(Boolean);
    if (plays.length > 0 && plays.every((p) => isBullionPlayFullyDone(p!))) {
        return true;
    }
    return false;
}

export type BullionResolvedMatchFixtureState = {
    phase: BullionLobbyFixturePhase;
    matchFinished: boolean;
    kickoffPassed: boolean;
};

function roomStatusIndicatesFinished(status: string | undefined | null): boolean {
    const st = String(status ?? "").toUpperCase();
    return (
        st.includes("MATCH COMPLETE") ||
        st.includes("FINISHED") ||
        st.includes("FULL TIME") ||
        st.includes("EVENT CLOSED") ||
        st === "EVENT_FULL_RESULT" ||
        st.includes("ABANDONED")
    );
}

/**
 * Lobby / match-landing / in-room board: merge list row, live socket, and settled contests.
 * Fixes stale "Match Scheduled" trade rows still showing prematch UI after full time.
 */
export type ResolveBullionMatchFixtureStateOpts = {
    nowMs?: number;
    socket?: BullionFixtureSocketSignals | null;
    contests?: BullionFixtureContestSignals | null;
    /** When false, settled contests alone do not mark the fixture finished (avoids blocking the board while `/bullion/my-play` loads). */
    useContestSignals?: boolean;
};

export function resolveBullionMatchFixtureState(
    room: Pick<BullionRoom, "fixturePhase" | "status" | "isLive" | "startsAt"> | null | undefined,
    opts?: ResolveBullionMatchFixtureStateOpts
): BullionResolvedMatchFixtureState {
    const nowMs = opts?.nowMs ?? Date.now();
    if (!room) {
        return { phase: "scheduled", matchFinished: false, kickoffPassed: false };
    }

    const fromSocket = bullionFixtureFinishedFromSocketSignals(opts?.socket);
    const fromContests =
        opts?.useContestSignals === false
            ? false
            : bullionFixtureFinishedFromContestSignals(opts?.contests);
    const fromRoom =
        room.fixturePhase === "finished" || roomStatusIndicatesFinished(room.status);
    const startsAtTs = room.startsAt ? new Date(room.startsAt).getTime() : Number.NaN;
    const scheduledFuture = Number.isFinite(startsAtTs) && startsAtTs > nowMs;
    /** Stale list rows / settled contests must not mark a future kickoff as finished for entry lock. */
    const matchFinished =
        fromSocket || (!scheduledFuture && (fromRoom || fromContests));

    if (matchFinished) {
        return { phase: "finished", matchFinished: true, kickoffPassed: true };
    }

    const phase = bullionRoomResolvedFixturePhase(room);
    const kickoffFromClock = Number.isFinite(startsAtTs) ? startsAtTs <= nowMs : false;
    const kickoffPassed = kickoffFromClock || Boolean(room.isLive) || phase === "live";

    return { phase, matchFinished: false, kickoffPassed };
}

/** Apply resolved phase onto a room object for downstream `fixturePhase` / lock helpers. */
export function bullionRoomWithResolvedFixturePhase<T extends BullionRoom>(
    room: T,
    resolved: Pick<BullionResolvedMatchFixtureState, "phase" | "matchFinished">
): T {
    if (!resolved.matchFinished && resolved.phase === bullionRoomResolvedFixturePhase(room)) {
        return room;
    }
    return {
        ...room,
        fixturePhase: resolved.phase,
        isLive: resolved.matchFinished ? false : room.isLive,
        ...(resolved.matchFinished
            ? {
                  status: roomStatusIndicatesFinished(room.status)
                      ? room.status
                      : "MATCH COMPLETE",
              }
            : null),
    };
}

/** Lobby rows: mark fixtures finished when all of the user's games on that match are settled. */
export function enrichBullionLobbyRoomsWithMyGames(
    rooms: BullionRoom[],
    activeBullionGamesByMatchId: Record<string, BullionLobbyUserActiveGame[]>
): BullionRoom[] {
    return rooms.map((room) => {
        const games = findBullionLobbyGamesForMatchId(room.matchId, activeBullionGamesByMatchId);
        const resolved = resolveBullionMatchFixtureState(room, {
            contests: { gameStatuses: games.map((g) => g.gameStatus) },
        });
        return bullionRoomWithResolvedFixturePhase(room, resolved);
    });
}
